import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, MinusIcon, PackageIcon, AlertTriangleIcon, AlertCircleIcon, LayersIcon, EyeIcon, WrenchIcon, XIcon, TrashIcon, ArchiveIcon, SearchIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { DataTable, Column } from '../components/DataTable';
import { mockStockEntries, mockProducts } from '../data/mockData';
interface StockBalance {
  id: string;
  productCode: string;
  productName: string;
  warehouse: string;
  currentQty: number;
  unitOfMeasure: string;
  minStockLevel: number;
  reorderPoint: number;
  status: 'In Stock' | 'Low Stock' | 'Critical';
}
type TabFilter = 'All' | 'In Stock' | 'Low Stock' | 'Critical';
export function StockList() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabFilter>('All');
  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([]);
  const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState(false);
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const warehouseDropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (warehouseDropdownRef.current && !warehouseDropdownRef.current.contains(e.target as Node)) {
        setWarehouseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggleWarehouse = (wh: string) => {
    setSelectedWarehouses(prev => prev.includes(wh) ? prev.filter(w => w !== wh) : [...prev, wh]);
  };
  // Disposal modal state
  const [selectedItem, setSelectedItem] = useState<StockBalance | null>(null);
  const [disposalQty, setDisposalQty] = useState(1);
  const [disposalRemarks, setDisposalRemarks] = useState('');
  const openDisposalModal = (item: StockBalance) => {
    setSelectedItem(item);
    setDisposalQty(1);
    setDisposalRemarks('');
  };
  const closeDisposalModal = () => {
    setSelectedItem(null);
  };
  const handleDisposalSubmit = () => {
    if (selectedItem) {
      alert(`Send to Disposal / Repair request submitted!\n\nProduct: ${selectedItem.productName}\nWarehouse: ${selectedItem.warehouse}\nQuantity: ${disposalQty} ${selectedItem.unitOfMeasure}\nRemarks: ${disposalRemarks || '—'}`);
      closeDisposalModal();
    }
  };
  // Compute aggregated stock balances
  const stockBalances = useMemo(() => {
    const balances = new Map<string, number>();
    // Calculate net quantities per product-warehouse combination
    mockStockEntries.forEach(entry => {
      if (entry.status !== 'Completed') return;
      const key = `${entry.productCode}|${entry.warehouse}`;
      const current = balances.get(key) || 0;
      const amount = entry.type === 'Stock In' ? entry.quantity : -entry.quantity;
      balances.set(key, current + amount);
    });
    // Map to rich objects with product details
    const result: StockBalance[] = [];
    balances.forEach((qty, key) => {
      const [productCode, warehouse] = key.split('|');
      const product = mockProducts.find(p => p.productCode === productCode);
      if (product) {
        let status: StockBalance['status'] = 'In Stock';
        if (qty <= product.minStockLevel) {
          status = 'Critical';
        } else if (qty <= product.reorderPoint) {
          status = 'Low Stock';
        }
        result.push({
          id: key,
          productCode,
          productName: product.productName,
          warehouse,
          currentQty: qty,
          unitOfMeasure: product.unitOfMeasure,
          minStockLevel: product.minStockLevel,
          reorderPoint: product.reorderPoint,
          status
        });
      }
    });
    return result;
  }, []);
  // Get unique warehouses for filter
  const warehouses = useMemo(() => {
    const unique = [...new Set(stockBalances.map(b => b.warehouse))];
    return unique.sort();
  }, [stockBalances]);
  // Filter data based on warehouse and active tab
  const warehouseFiltered = selectedWarehouses.length === 0 ? stockBalances : stockBalances.filter(b => selectedWarehouses.includes(b.warehouse));
  // Calculate summary stats (based on warehouse-filtered data)
  const totalProducts = new Set(warehouseFiltered.map(b => b.productCode)).size;
  const totalQuantity = warehouseFiltered.reduce((sum, b) => sum + b.currentQty, 0);
  const lowStockCount = warehouseFiltered.filter(b => b.status === 'Low Stock').length;
  const criticalCount = warehouseFiltered.filter(b => b.status === 'Critical').length;
  const filteredData = activeTab === 'All' ? warehouseFiltered : warehouseFiltered.filter(b => b.status === activeTab);
  const columns: Column<StockBalance>[] = [{
    key: 'productCode',
    label: 'Product Code',
    render: item => <span className="font-medium text-gray-900" data-id="element-2452">{item.productCode}</span>
  }, {
    key: 'productName',
    label: 'Product Name',
    render: item => <div className="max-w-xs truncate" title={item.productName} data-id="element-2453">
          {item.productName}
        </div>
  }, {
    key: 'warehouse',
    label: 'Warehouse'
  }, {
    key: 'currentQty',
    label: 'Current Qty',
    render: item => <span className={`font-bold ${item.status === 'Critical' ? 'text-red-600' : item.status === 'Low Stock' ? 'text-yellow-600' : 'text-green-600'}`} data-id="element-2454">
          {item.currentQty.toLocaleString()}
        </span>
  }, {
    key: 'unitOfMeasure',
    label: 'UoM',
    render: item => <span className="text-gray-500" data-id="element-2455">{item.unitOfMeasure}</span>
  }, {
    key: 'minStockLevel',
    label: 'Min Level',
    render: item => <span className="text-gray-500" data-id="element-2456">{item.minStockLevel}</span>
  }, {
    key: 'reorderPoint',
    label: 'Reorder Pt',
    render: item => <span className="text-gray-500" data-id="element-2457">{item.reorderPoint}</span>
  }, {
    key: 'status',
    label: 'Status',
    render: item => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.status === 'In Stock' ? 'bg-green-100 text-green-800' : item.status === 'Low Stock' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-2458">
          {item.status}
        </span>
  }];
  const tabs: {
    key: TabFilter;
    label: string;
  }[] = [{
    key: 'All',
    label: 'All Inventory'
  }, {
    key: 'In Stock',
    label: 'Healthy Stock'
  }, {
    key: 'Low Stock',
    label: 'Low Stock'
  }, {
    key: 'Critical',
    label: 'Critical Level'
  }];
  return <div className="space-y-6" data-id="element-2459">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2460">
        <div data-id="element-2461">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2462">Stock</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2463">
            Current inventory levels across all warehouses.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-2464">
          <button onClick={() => navigate('/stock/in')} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700" data-id="element-2465">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-2466" />
            Record Gain
          </button>
          <button onClick={() => navigate('/stock/out')} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700" data-id="element-2467">
            <MinusIcon className="h-4 w-4 mr-2" data-id="element-2468" />
            Record Loss
          </button>
        </div>
      </div>

      {/* Warehouse Filter */}
      <div className="flex items-center gap-3" data-id="element-2469">
        <label className="text-sm font-medium text-gray-700" data-id="element-2470">Warehouse:</label>
        <div className="relative" ref={warehouseDropdownRef} data-id="element-2471">
          <button onClick={() => {
          setWarehouseDropdownOpen(!warehouseDropdownOpen);
          setWarehouseSearch('');
        }} className="inline-flex items-center justify-between min-w-[220px] rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-2472">
            <span className="truncate text-gray-700" data-id="element-2473">
              {selectedWarehouses.length === 0 ? 'All Warehouses' : selectedWarehouses.length === 1 ? selectedWarehouses[0] : `${selectedWarehouses.length} warehouses selected`}
            </span>
            <ChevronDownIcon className={`h-4 w-4 ml-2 text-gray-400 transition-transform ${warehouseDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2474" />
          </button>

          {warehouseDropdownOpen && <div className="absolute z-20 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg" data-id="element-2475">
              {/* Search */}
              <div className="p-2 border-b border-gray-100" data-id="element-2476">
                <div className="relative" data-id="element-2477">
                  <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-2478" />
                  <input type="text" value={warehouseSearch} onChange={e => setWarehouseSearch(e.target.value)} placeholder="Search warehouses..." className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" autoFocus data-id="element-2479" />
                </div>
              </div>

              {/* Options */}
              <div className="max-h-48 overflow-y-auto py-1" data-id="element-2480">
                {warehouses.filter(wh => wh.toLowerCase().includes(warehouseSearch.toLowerCase())).map(wh => {
              const isSelected = selectedWarehouses.includes(wh);
              return <button key={wh} onClick={() => toggleWarehouse(wh)} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors" data-id="element-2481">
                        <div className={`flex-shrink-0 h-4 w-4 rounded border flex items-center justify-center transition-colors ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 bg-white'}`} data-id="element-2482">
                          {isSelected && <CheckIcon className="h-3 w-3 text-white" data-id="element-2483" />}
                        </div>
                        <span className={isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'} data-id="element-2484">
                          {wh}
                        </span>
                      </button>;
            })}
                {warehouses.filter(wh => wh.toLowerCase().includes(warehouseSearch.toLowerCase())).length === 0 && <p className="px-3 py-2 text-sm text-gray-400" data-id="element-2485">
                    No warehouses found
                  </p>}
              </div>

              {/* Footer */}
              {selectedWarehouses.length > 0 && <div className="border-t border-gray-100 p-2" data-id="element-2486">
                  <button onClick={() => setSelectedWarehouses([])} className="w-full text-center text-xs text-indigo-600 hover:text-indigo-800 font-medium py-1" data-id="element-2487">
                    Clear all filters
                  </button>
                </div>}
            </div>}
        </div>

        {/* Selected warehouse tags */}
        {selectedWarehouses.length > 0 && <div className="flex flex-wrap gap-1.5" data-id="element-2488">
            {selectedWarehouses.map(wh => <span key={wh} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700" data-id="element-2489">
                {wh}
                <button onClick={() => toggleWarehouse(wh)} className="hover:text-indigo-900" data-id="element-2490">
                  <XIcon className="h-3 w-3" data-id="element-2491" />
                </button>
              </span>)}
          </div>}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2492">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2493">
          <div className="flex items-center" data-id="element-2494">
            <div className="flex-shrink-0 p-3 bg-indigo-100 rounded-lg" data-id="element-2495">
              <PackageIcon className="h-6 w-6 text-indigo-600" data-id="element-2496" />
            </div>
            <div className="ml-4" data-id="element-2497">
              <p className="text-sm font-medium text-gray-500" data-id="element-2498">
                Total Products
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-2499">
                {totalProducts}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2500">
          <div className="flex items-center" data-id="element-2501">
            <div className="flex-shrink-0 p-3 bg-green-100 rounded-lg" data-id="element-2502">
              <LayersIcon className="h-6 w-6 text-green-600" data-id="element-2503" />
            </div>
            <div className="ml-4" data-id="element-2504">
              <p className="text-sm font-medium text-gray-500" data-id="element-2505">
                Total Quantity
              </p>
              <p className="text-2xl font-bold text-green-600" data-id="element-2506">
                {totalQuantity.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2507">
          <div className="flex items-center" data-id="element-2508">
            <div className="flex-shrink-0 p-3 bg-yellow-100 rounded-lg" data-id="element-2509">
              <AlertTriangleIcon className="h-6 w-6 text-yellow-600" data-id="element-2510" />
            </div>
            <div className="ml-4" data-id="element-2511">
              <p className="text-sm font-medium text-gray-500" data-id="element-2512">
                Low Stock Items
              </p>
              <p className="text-2xl font-bold text-yellow-600" data-id="element-2513">
                {lowStockCount}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2514">
          <div className="flex items-center" data-id="element-2515">
            <div className="flex-shrink-0 p-3 bg-red-100 rounded-lg" data-id="element-2516">
              <AlertCircleIcon className="h-6 w-6 text-red-600" data-id="element-2517" />
            </div>
            <div className="ml-4" data-id="element-2518">
              <p className="text-sm font-medium text-gray-500" data-id="element-2519">Critical</p>
              <p className="text-2xl font-bold text-red-600" data-id="element-2520">{criticalCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Filters */}
      <div className="border-b border-gray-200" data-id="element-2521">
        <nav className="-mb-px flex space-x-8" data-id="element-2522">
          {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.key ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2523">
              {tab.label}
              <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${activeTab === tab.key ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-2524">
                {tab.key === 'All' ? warehouseFiltered.length : warehouseFiltered.filter(b => b.status === tab.key).length}
              </span>
            </button>)}
        </nav>
      </div>

      {/* Data Table */}
      <div className="h-[500px]" data-id="element-2525">
        <DataTable columns={columns} data={filteredData} keyExtractor={item => item.id} searchPlaceholder="Search inventory..." actions={item => <div className="flex items-center gap-2 justify-end" data-id="element-2527">
              <button onClick={() => alert(`View details for ${item.productName}`)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50" data-id="element-2528">
                <EyeIcon className="h-3.5 w-3.5 mr-1" data-id="element-2529" /> View
              </button>
              <button onClick={() => openDisposalModal(item)} className="inline-flex items-center px-2.5 py-1.5 border border-orange-300 shadow-sm text-xs font-medium rounded text-orange-700 bg-orange-50 hover:bg-orange-100" data-id="element-2530">
                <WrenchIcon className="h-3.5 w-3.5 mr-1" data-id="element-2531" /> Send to Disposal /
                Repair
              </button>
            </div>} data-id="element-2526" />
      </div>

      {/* Disposal / Repair Modal */}
      {selectedItem && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2532">
          <div className="flex min-h-full items-center justify-center p-4" data-id="element-2533">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" onClick={closeDisposalModal} data-id="element-2534" />

            {/* Modal */}
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg z-10" data-id="element-2535">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-2536">
                <div className="flex items-center gap-3" data-id="element-2537">
                  <div className="p-2 bg-orange-100 rounded-lg" data-id="element-2538">
                    <WrenchIcon className="h-5 w-5 text-orange-600" data-id="element-2539" />
                  </div>
                  <div data-id="element-2540">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-2541">
                      Send to Disposal / Repair
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-2542">
                      Specify quantity to send
                    </p>
                  </div>
                </div>
                <button onClick={closeDisposalModal} className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100" data-id="element-2543">
                  <XIcon className="h-5 w-5" data-id="element-2544" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-5" data-id="element-2545">
                {/* Product Info */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2546">
                  <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2547">
                    <div data-id="element-2548">
                      <p className="text-gray-500" data-id="element-2549">Product</p>
                      <p className="font-medium text-gray-900" data-id="element-2550">
                        {selectedItem.productName}
                      </p>
                    </div>
                    <div data-id="element-2551">
                      <p className="text-gray-500" data-id="element-2552">Product Code</p>
                      <p className="font-medium text-gray-900" data-id="element-2553">
                        {selectedItem.productCode}
                      </p>
                    </div>
                    <div data-id="element-2554">
                      <p className="text-gray-500" data-id="element-2555">Warehouse</p>
                      <p className="font-medium text-gray-900" data-id="element-2556">
                        {selectedItem.warehouse}
                      </p>
                    </div>
                    <div data-id="element-2557">
                      <p className="text-gray-500" data-id="element-2558">Available Qty</p>
                      <p className="font-bold text-indigo-600" data-id="element-2559">
                        {selectedItem.currentQty} {selectedItem.unitOfMeasure}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quantity */}
                <div data-id="element-2560">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2561">
                    Quantity to send
                  </label>
                  <div className="flex items-center gap-3" data-id="element-2562">
                    <input type="number" min={1} max={selectedItem.currentQty} value={disposalQty} onChange={e => {
                  const val = parseInt(e.target.value) || 0;
                  setDisposalQty(Math.min(Math.max(1, val), selectedItem.currentQty));
                }} className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm" data-id="element-2563" />
                    <span className="text-sm text-gray-500" data-id="element-2564">
                      of {selectedItem.currentQty} {selectedItem.unitOfMeasure}{' '}
                      available
                    </span>
                  </div>
                  {disposalQty === selectedItem.currentQty && <p className="mt-1 text-xs text-amber-600" data-id="element-2565">
                      ⚠ This will send the entire stock to disposal & repair.
                    </p>}
                </div>

                {/* Remarks */}
                <div data-id="element-2566">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2567">
                    Remarks <span className="text-gray-400" data-id="element-2568">(optional)</span>
                  </label>
                  <textarea rows={2} value={disposalRemarks} onChange={e => setDisposalRemarks(e.target.value)} placeholder="Reason for disposal or repair..." className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm" data-id="element-2569" />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg" data-id="element-2570">
                <button onClick={closeDisposalModal} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2571">
                  Cancel
                </button>
                <button onClick={handleDisposalSubmit} className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700" data-id="element-2572">
                  <WrenchIcon className="h-4 w-4 mr-1.5 inline" data-id="element-2573" />
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}